import React, { useState, useEffect, useCallback } from 'react';
import { apiService, GolfCourseImage } from '../../../services/api';
import { FormData, UploadingImage, defaultCpsConfig, defaultChronogolfConfig } from './types';

interface FormStateManagerProps {
  course?: any;
  children: (state: FormState, actions: FormActions) => React.ReactNode;
}

export interface FormState {
  // Form data
  formData: FormData;
  originalFormData: FormData | null;
  hasUnsavedChanges: boolean;
  
  // UI states
  isLoading: boolean;
  error: string | null;
  validationResult: { valid: boolean; message: string } | null;
  
  // Dialog states
  showApiTypeDialog: boolean;
  pendingApiType: 'cps' | 'chronogolf' | null;
  showExitDialog: boolean;
  
  // Image management states
  existingImages: GolfCourseImage[];
  uploadingImages: UploadingImage[];
  deletingImageIds: Set<number>;
  isReordering: boolean;
  
  // Computed states
  isEditing: boolean;
}

export interface FormActions {
  // Form data actions
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  resetFormData: () => void;
  
  // UI actions
  setIsLoading: (loading: boolean) => void;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setValidationResult: React.Dispatch<React.SetStateAction<{ valid: boolean; message: string } | null>>;
  
  // Dialog actions
  setShowApiTypeDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingApiType: React.Dispatch<React.SetStateAction<'cps' | 'chronogolf' | null>>;
  setShowExitDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleApiTypeChange: (apiType: 'cps' | 'chronogolf') => void;
  confirmApiTypeChange: () => void;
  cancelApiTypeChange: () => void;
  
  // Exit handling
  handleExit: () => void;
  confirmExit: () => void;
  cancelExit: () => void;
  
  // Image management actions
  setExistingImages: React.Dispatch<React.SetStateAction<GolfCourseImage[]>>;
  setUploadingImages: React.Dispatch<React.SetStateAction<UploadingImage[]>>;
  setDeletingImageIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  setIsReordering: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Form submission
  handleSubmit: (e: React.FormEvent, onSuccess: () => void) => Promise<void>;
  
  // Data loading
  loadCourseDetails: (courseId: number) => Promise<void>;
}

export const FormStateManager: React.FC<FormStateManagerProps> = ({ course, children }) => {
  // Form data state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    location: '',
    description: '',
    api_type: 'cps',
    api_course_id: '',
    api_config: defaultCpsConfig,
    booking_advance_days: 30,
    booking_open_time: '00:00',
    holes: undefined,
    length_yards: undefined,
    par: undefined,
    course_rating: undefined,
    slope_rating: undefined,
    course_type: '',
    is_public: true,
    facilities: [],
    features: [],
    address: '',
    phone: '',
    email: '',
    website: ''
  });
  
  // Form state tracking
  const [originalFormData, setOriginalFormData] = useState<FormData | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; message: string } | null>(null);
  
  // Dialog states
  const [showApiTypeDialog, setShowApiTypeDialog] = useState(false);
  const [pendingApiType, setPendingApiType] = useState<'cps' | 'chronogolf' | null>(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  
  // Image management state
  const [existingImages, setExistingImages] = useState<GolfCourseImage[]>([]);
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]); 
  const [deletingImageIds, setDeletingImageIds] = useState<Set<number>>(new Set());
  const [isReordering, setIsReordering] = useState(false);

  const isEditing = !!course;

  // Deep comparison function to detect changes
  const deepEqual = useCallback((obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== typeof obj2) return false;
    
    if (Array.isArray(obj1)) {
      if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
      return obj1.every((item, index) => deepEqual(item, obj2[index]));
    }
    
    if (typeof obj1 === 'object') {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;
      return keys1.every(key => deepEqual(obj1[key], obj2[key]));
    }
    
    return obj1 === obj2;
  }, []);

  // Track form changes - only for basic info fields, excluding image operations
  useEffect(() => {
    if (originalFormData && !isLoading) {
      const hasChanges = !deepEqual(formData, originalFormData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, originalFormData, isLoading, deepEqual]);

  // Load course data on mount
  useEffect(() => {
    if (course) {
      loadCourseDetails(course.id);
    } else {
      setOriginalFormData(JSON.parse(JSON.stringify(formData)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  // Load complete course details
  const loadCourseDetails = useCallback(async (courseId: number) => {
    try {
      const courseDetails = await apiService.getAdminGolfCourse(courseId);
      
      const loadedFormData = {
        name: courseDetails.name,
        location: courseDetails.location || '',
        description: courseDetails.description || '',
        api_type: courseDetails.api_type as 'cps' | 'chronogolf',
        api_course_id: courseDetails.api_course_id || '',
        api_config: (courseDetails.api_config && Object.keys(courseDetails.api_config).length > 0)
          ? courseDetails.api_config
          : (courseDetails.api_type === 'cps' ? defaultCpsConfig : defaultChronogolfConfig),
        booking_url: courseDetails.booking_url || '',
        booking_advance_days: courseDetails.booking_advance_days || 30,
        booking_open_time: courseDetails.booking_open_time || '00:00',
        holes: courseDetails.holes || undefined,
        length_yards: courseDetails.length_yards || undefined,
        par: courseDetails.par || undefined,
        course_rating: courseDetails.course_rating || undefined,
        slope_rating: courseDetails.slope_rating || undefined,
        course_type: courseDetails.course_type || '',
        is_public: courseDetails.is_public ?? true,
        facilities: courseDetails.facilities || [],
        features: courseDetails.features || [],
        address: courseDetails.address || '',
        phone: courseDetails.phone || '',
        email: courseDetails.email || '',
        website: courseDetails.website || ''
      };
      
      setFormData(loadedFormData);
      setOriginalFormData(JSON.parse(JSON.stringify(loadedFormData)));
      
      // Load existing images if editing
      if (courseDetails.images) {
        try {
          // Images are already included in the courseDetails response
          const images = Array.isArray(courseDetails.images) ? courseDetails.images : [];
          // Only convert if the images are actually GolfCourseImage objects
          if (images.length > 0 && typeof images[0] === 'object' && 'id' in images[0]) {
            setExistingImages(images as GolfCourseImage[]);
          } else {
            // If they are strings, we need to convert them or fetch proper image data
            setExistingImages([]);
          }
        } catch (error) {
          console.error('Failed to load course images:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load course details:', error);
      if (course) {
        const basicFormData = {
          name: course.name,
          location: course.location,
          description: course.description || '',
          api_type: course.api_type,
          api_course_id: course.api_course_id || '',
          api_config: course.api_config,
          booking_url: course.booking_url || '',
          booking_advance_days: course.booking_advance_days || 30,
          booking_open_time: course.booking_open_time || '00:00',
          holes: undefined,
          length_yards: undefined,
          par: undefined,
          course_rating: undefined,
          slope_rating: undefined,
          course_type: '',
          is_public: true,
          facilities: [],
          features: [],
          address: '',
          phone: '',
          email: '',
          website: ''
        };
        setFormData(basicFormData);
        setOriginalFormData(JSON.parse(JSON.stringify(basicFormData)));
      }
    }
  }, [course]);

  // Form submission
  const handleSubmit = useCallback(async (e: React.FormEvent, onSuccess: () => void) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await apiService.updateGolfCourse(course!.id, formData);
      } else {
        await apiService.createGolfCourse(formData);
      }
      
      // Reset unsaved changes state after successful save
      setOriginalFormData(JSON.parse(JSON.stringify(formData)));
      setHasUnsavedChanges(false);
      
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }, [isEditing, course, formData]);

  // API type change handlers
  const handleApiTypeChange = useCallback((apiType: 'cps' | 'chronogolf') => {
    if (isEditing) {
      setPendingApiType(apiType);
      setShowApiTypeDialog(true);
    } else {
      setFormData(prev => ({
        ...prev,
        api_type: apiType,
        api_config: apiType === 'cps' ? defaultCpsConfig : defaultChronogolfConfig
      }));
    }
  }, [isEditing]);

  const confirmApiTypeChange = useCallback(() => {
    if (pendingApiType) {
      setFormData(prev => ({
        ...prev,
        api_type: pendingApiType,
        api_config: pendingApiType === 'cps' ? defaultCpsConfig : defaultChronogolfConfig
      }));
    }
    setShowApiTypeDialog(false);
    setPendingApiType(null);
  }, [pendingApiType]);

  const cancelApiTypeChange = useCallback(() => {
    setShowApiTypeDialog(false);
    setPendingApiType(null);
  }, []);

  // Exit protection handlers
  const handleExit = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      // onCancel(); // This will be handled by the parent component
    }
  }, [hasUnsavedChanges]);

  const confirmExit = useCallback(() => {
    setShowExitDialog(false);
    // onCancel(); // This will be handled by the parent component
  }, []);

  const cancelExit = useCallback(() => {
    setShowExitDialog(false);
  }, []);

  const resetFormData = useCallback(() => {
    setFormData({
      name: '',
      location: '',
      description: '',
      api_type: 'cps',
      api_course_id: '',
      api_config: defaultCpsConfig,
      booking_advance_days: 30,
      booking_open_time: '00:00',
      holes: undefined,
      length_yards: undefined,
      par: undefined,
      course_rating: undefined,
      slope_rating: undefined,
      course_type: '',
      is_public: true,
      facilities: [],
      features: [],
      address: '',
      phone: '',
      email: '',
      website: ''
    });
  }, []);

  const state: FormState = {
    formData,
    originalFormData,
    hasUnsavedChanges,
    isLoading,
    error,
    validationResult,
    showApiTypeDialog,
    pendingApiType,
    showExitDialog,
    existingImages,
    uploadingImages,
    deletingImageIds,
    isReordering,
    isEditing
  };

  const actions: FormActions = {
    setFormData,
    resetFormData,
    setIsLoading,
    setError,
    setValidationResult,
    setShowApiTypeDialog,
    setPendingApiType,
    setShowExitDialog,
    handleApiTypeChange,
    confirmApiTypeChange,
    cancelApiTypeChange,
    handleExit,
    confirmExit,
    cancelExit,
    setExistingImages,
    setUploadingImages,
    setDeletingImageIds,
    setIsReordering,
    handleSubmit,
    loadCourseDetails
  };

  return <>{children(state, actions)}</>;
};